import * as THREE from "three";

import { PhysicsTable } from "../../../entities/physics_table.js";
import { PhysicsCube } from "../../../entities/physics_cube.js";
import type { LevelLayer } from "../../../level_config.js";
import type { EscalatorConfig } from "../../../conveyor/conveyor_mode.js";
import type { LevelEntity } from "../../../types/level_entity.js";
import { ConveyorArt } from "./conveyor_art.js";
import { EscalatorWalls } from "./escalator_walls.js";
import { EscalatorButton } from "./escalator_button.js";
import { EscalatorLamp } from "./escalator_lamp.js";
import { EscalatorStructures } from "./escalator_structures.js";
import type { EscalatorStructure } from "./escalator_structures.js";
import { ConveyorConfigService } from "../../../conveyor_config/conveyor_config_service.js";
import type { ConveyorConfig } from "../../../conveyor_config/conveyor_config_service.js";

const ESCALATOR_DEFAULTS: Required<Omit<EscalatorConfig, "levels">> = {
  gapCubes: 1,
};

export class Escalator
{
  public static readonly BELT_LIFT_CUBES = 1;
  public static readonly BELT_MARGIN_LAYERS = 1;

  private static readonly HIDE_GAP_CUBES = 0.4;
  private static readonly HIDE_GAP_DEPTH_SHARE = 0.35;
  private static readonly POCKET_SLACK_CUBES = 0.5;
  private static readonly WALL_GAP = 0.03;
  private static readonly OPENING_MARGIN_ROWS = 1;
  private static readonly STALL_SPEED_SHARE = 0.3;
  private static readonly PILE_SLACK_CUBES = 0.5;
  private static readonly GAUGE_EASE = 4;
  private static readonly STOPPED = new THREE.Vector3();
  private static readonly RIDE_SPEED_TOLERANCE = 0.25;
  private static readonly RIDE_SPEED_SHARE = 0.2;

  private readonly config: Required<Omit<EscalatorConfig, "levels">>;
  private readonly settings: ConveyorConfig = ConveyorConfigService.get();
  private readonly velocity = new THREE.Vector3();
  private readonly carried_velocity = new THREE.Vector3();
  private riders: LevelEntity[] = [];
  private readonly step_x: number;
  private readonly surface_y: number;
  private readonly center_z: number;
  private direction!: THREE.Vector3;
  private spawn_edge_x = 0;
  private entry_x = 0;
  private visible_min_x = 0;
  private visible_max_x = 0;
  private mirrored: boolean;

  private art!: ConveyorArt;
  private walls!: EscalatorWalls;
  private button!: EscalatorButton;
  private lamp!: EscalatorLamp;

  private upcoming: EscalatorStructure;
  private wave_left: number;
  private breath_left = 0;
  private survived_sec = 0;
  private time_limit_sec = Infinity;
  private speed_multiplier = 1;
  private freeze_left = 0;
  private progress = 0;
  private jammed_sec = 0;
  private over = false;
  private won = false;
  private pressed = false;
  private paused = false;
  private stuck = false;

  public static deckLayers( cols: number, structures: EscalatorStructures ): LevelLayer[]
  {
    const depth = structures.maxDepth() + Escalator.BELT_MARGIN_LAYERS;

    return Array.from( { length: depth }, () => ( { grid: [ new Array( cols ).fill( 0 ) ], pieces: [] } ) );
  }

  public static beltLift( cube_size: THREE.Vector3 ): number
  {
    return cube_size.y * Escalator.BELT_LIFT_CUBES;
  }

  public static spawnPocket( cube_size: THREE.Vector3, belt_depth: number, structures: EscalatorStructures ): number
  {
    const step_x = cube_size.x * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    const slack = cube_size.x * Escalator.POCKET_SLACK_CUBES;

    return Escalator.hideGap( cube_size, belt_depth ) + structures.maxCols() * step_x + slack;
  }

  private static hideGap( cube_size: THREE.Vector3, belt_depth: number ): number
  {
    return cube_size.x * Escalator.HIDE_GAP_CUBES + belt_depth * Escalator.HIDE_GAP_DEPTH_SHARE;
  }

  constructor(
    private readonly table: PhysicsTable,
    config: EscalatorConfig,
    private readonly cube_size: THREE.Vector3,
    private readonly pocket: number,
    private readonly structures: EscalatorStructures,
    private readonly spawn_structure: ( layers: LevelLayer[], anchor: THREE.Vector3 ) => readonly LevelEntity[],
    mirrored: boolean = false
  )
  {
    this.config = { ...ESCALATOR_DEFAULTS, ...config };
    this.mirrored = mirrored;

    const top_center = table.getTopCenter();

    this.step_x = cube_size.x * ( 1 + PhysicsCube.GRID_GAP_RATIO );
    this.surface_y = table.surfaceYAt( top_center.x, top_center.z );
    this.center_z = top_center.z;
    this.upcoming = structures.next();
    this.wave_left = this.rollWave();

    this.build();

    table.setModelVisible( false );
    table.setLegSolid( false );
  }

  // The table must already stand where the new side wants it (mirrorX); the
  // run itself — time, gauge, the next structure — carries over untouched.
  public setMirrored( mirrored: boolean ): void
  {
    if ( mirrored === this.mirrored )
      return;

    this.mirrored = mirrored;
    this.disposeParts();
    this.build();

    this.button.setProgress( this.progress );

    if ( this.pressed )
    {
      this.button.press();
      this.lamp.startBlinking();
    }
  }

  private build(): void
  {
    const mirrored = this.mirrored;
    const dir = mirrored ? -1 : 1;

    const tilt = this.table.getTiltRad();
    const top_center = this.table.getTopCenter();
    const top_size = this.table.getTopSize();
    const deck_left_x = top_center.x - top_size.x * 0.5;
    const deck_right_x = top_center.x + top_size.x * 0.5;
    const wall_inner_x = mirrored ? deck_right_x - this.pocket : deck_left_x + this.pocket;
    const wall_z = top_center.z + top_size.z * 0.5 + Escalator.WALL_GAP;

    this.direction = new THREE.Vector3( Math.cos( tilt ) * dir, -Math.sin( tilt ), 0 );
    this.spawn_edge_x = wall_inner_x - dir * Escalator.hideGap( this.cube_size, top_size.z );
    this.entry_x = wall_inner_x;

    this.art = new ConveyorArt( deck_left_x, deck_right_x, this.surface_y, top_center.z, top_size.z, this.cube_size, dir );
    const belt_back_z = top_center.z - top_size.z * 0.5;
    const opening_rows = this.structures.maxRows() + Escalator.OPENING_MARGIN_ROWS;
    const opening_top_y = this.surface_y + this.cube_size.y * opening_rows;

    const inner_left_x = mirrored ? deck_left_x : wall_inner_x;
    const inner_right_x = mirrored ? wall_inner_x : deck_right_x;

    this.visible_min_x = inner_left_x;
    this.visible_max_x = inner_right_x;

    this.walls = new EscalatorWalls( inner_left_x, inner_right_x, this.surface_y, wall_z, this.cube_size, { belt_back_z, opening_top_y }, mirrored );
    this.button = new EscalatorButton( this.table, this.cube_size, mirrored );

    const lamp_spot = this.button.getLampSpot();
    this.lamp = new EscalatorLamp( lamp_spot.x, lamp_spot.z, lamp_spot.y, this.button.getSurfaceY() );
  }

  private disposeParts(): void
  {
    this.art.dispose();
    this.walls.dispose();
    this.button.dispose();
    this.lamp.dispose();
  }

  public isMirrored(): boolean
  {
    return this.mirrored;
  }

  public isPressed(): boolean
  {
    return this.pressed;
  }

  public isOver(): boolean
  {
    return this.over;
  }

  public isWon(): boolean
  {
    return this.won;
  }

  public setTimeLimit( seconds: number ): void
  {
    this.time_limit_sec = seconds;
  }

  public setSpeedMultiplier( multiplier: number ): void
  {
    this.speed_multiplier = multiplier;
  }

  public getTimeLimit(): number
  {
    return this.time_limit_sec;
  }

  public getRemainingSeconds(): number
  {
    return Math.max( 0, Math.ceil( this.time_limit_sec - this.survived_sec ) );
  }

  public freeze(): boolean
  {
    if ( this.over || this.isFrozen() )
      return false;

    this.freeze_left = this.settings.freeze_sec;
    return true;
  }

  public isFrozen(): boolean
  {
    return this.freeze_left > 0;
  }

  public getFreezeSeconds(): number
  {
    return this.settings.freeze_sec;
  }

  public getFreezeShare(): number
  {
    return this.freeze_left / this.settings.freeze_sec;
  }

  public setPaused( paused: boolean ): void
  {
    this.paused = paused;
  }

  public isVisibleX( x: number ): boolean
  {
    return x > this.visible_min_x && x < this.visible_max_x;
  }

  public hasEntityPastCenter( entities: readonly LevelEntity[] ): boolean
  {
    const center_x = ( this.visible_min_x + this.visible_max_x ) / 2;
    const dir = this.mirrored ? -1 : 1;

    return entities.some( e => Escalator.hasLiveBody( e ) && ( e.body.translation().x - center_x ) * dir >= 0 );
  }

  public getSurvivedSeconds(): number
  {
    return Math.floor( this.survived_sec );
  }

  public getSpeed(): number
  {
    const share = THREE.MathUtils.clamp( this.survived_sec / this.settings.ramp_sec, 0, 1 );
    const start_speed = this.settings.start_speed * this.speed_multiplier;
    const max_speed = Math.max( start_speed, this.settings.max_speed );

    return THREE.MathUtils.lerp( start_speed, max_speed, share );
  }

  public step( dt: number ): void
  {
    this.table.driveSurface( this.beltVelocity(), dt );
  }

  private beltVelocity(): THREE.Vector3
  {
    if ( this.over || this.paused || this.stuck || this.isFrozen() )
      return Escalator.STOPPED;

    return this.velocity.copy( this.direction ).multiplyScalar( this.getSpeed() );
  }

  private launch( entities: readonly LevelEntity[] ): void
  {
    const velocity = this.beltVelocity();

    for ( const entity of entities )
    {
      if ( Escalator.hasLiveBody( entity ) )
        entity.body.setLinvel( { x: velocity.x, y: velocity.y, z: velocity.z }, true );
    }
  }

  private carryRiders( entities: readonly LevelEntity[] ): void
  {
    const velocity = this.beltVelocity();
    const was_moving = this.carried_velocity.lengthSq() > 0;
    const is_moving = velocity.lengthSq() > 0;

    if ( was_moving && !is_moving )
    {
      this.riders = entities.filter( e => this.isRiding( e, this.carried_velocity ) );
      Escalator.shiftVelocity( this.riders, this.carried_velocity, -1 );
    }
    else if ( !was_moving && is_moving )
    {
      Escalator.shiftVelocity( this.riders.filter( e => this.isRiding( e, Escalator.STOPPED ) ), velocity, 1 );
      this.riders = [];
    }

    this.carried_velocity.copy( velocity );
  }

  private isRiding( entity: LevelEntity, belt_velocity: THREE.Vector3 ): boolean
  {
    if ( !Escalator.hasLiveBody( entity ) )
      return false;

    const p = entity.body.translation();

    if ( p.y <= this.surface_y || this.table.isOutside( p ) )
      return false;

    const v = entity.body.linvel();
    const tolerance = Math.max( Escalator.RIDE_SPEED_TOLERANCE, belt_velocity.length() * Escalator.RIDE_SPEED_SHARE );
    const dx = v.x - belt_velocity.x;
    const dy = v.y - belt_velocity.y;
    const dz = v.z - belt_velocity.z;

    return dx * dx + dy * dy + dz * dz < tolerance * tolerance;
  }

  private static shiftVelocity( entities: readonly LevelEntity[], by: THREE.Vector3, sign: number ): void
  {
    for ( const entity of entities )
    {
      const v = entity.body.linvel();
      entity.body.setLinvel( { x: v.x + by.x * sign, y: v.y + by.y * sign, z: v.z + by.z * sign }, true );
    }
  }

  public update( dt: number, entities: readonly LevelEntity[] ): void
  {
    this.carryRiders( entities );

    if ( this.paused )
      return;

    this.lamp.update( dt );

    if ( this.isOver() )
      return;

    const reached = entities.some( e => Escalator.hasLiveBody( e ) && this.button.isReachedBy( e.body.translation(), e.size ) );

    if ( reached && !this.pressed )
      this.press();
    else if ( !reached && this.pressed )
      this.release();

    this.survived_sec += dt;

    if ( this.survived_sec >= this.time_limit_sec )
    {
      this.survived_sec = this.time_limit_sec;
      this.won = true;
      this.over = true;

      if ( this.pressed )
        this.release();

      return;
    }

    if ( this.isFrozen() )
    {
      this.freeze_left = Math.max( 0, this.freeze_left - dt );
      this.art.update( dt, 0 );
      return;
    }

    if ( !this.stuck )
      this.breath_left = Math.max( 0, this.breath_left - dt );

    this.art.update( dt, this.stuck ? 0 : this.getSpeed() );
    this.updateSpawn( entities );
    this.updateJam( dt, entities );
  }

  public dispose(): void
  {
    this.disposeParts();
  }

  private press(): void
  {
    this.pressed = true;
    this.button.press();
    this.lamp.startBlinking();
  }

  private release(): void
  {
    this.pressed = false;
    this.button.release();
    this.lamp.stopBlinking();
  }

  private updateJam( dt: number, entities: readonly LevelEntity[] ): void
  {
    const target = this.pressed ? this.pileShare( entities ) : 0;

    this.stuck = target >= 1;
    this.jammed_sec = this.stuck ? this.jammed_sec + dt : 0;

    if ( this.jammed_sec >= this.settings.fill_sec )
    {
      this.over = true;
      this.progress = 1;
      this.button.setProgress( this.progress );
      return;
    }

    const ease = Math.min( 1, dt * Escalator.GAUGE_EASE );

    this.progress = THREE.MathUtils.lerp( this.progress, target, ease );
    this.button.setProgress( this.progress );
  }

  private pileShare( entities: readonly LevelEntity[] ): number
  {
    const dir = this.mirrored ? -1 : 1;
    const face_x = this.button.getFaceX();
    const visible_length = dir * ( face_x - this.entry_x );
    const slack = this.step_x * Escalator.PILE_SLACK_CUBES;

    const spans = entities
      .filter( e => this.isStalledOnBelt( e ) )
      .map( e =>
      {
        const distance = dir * ( face_x - e.body.translation().x );
        return { near: distance - e.size.x * 0.5, far: distance + e.size.x * 0.5 };
      } )
      .filter( span => span.near < visible_length )
      .sort( ( a, b ) => a.near - b.near );

    let pile_length = 0;

    for ( const span of spans )
    {
      if ( span.near > pile_length + slack )
        break;

      pile_length = Math.max( pile_length, span.far );
    }

    return THREE.MathUtils.clamp( pile_length / ( visible_length - slack ), 0, 1 );
  }

  private isStalledOnBelt( entity: LevelEntity ): boolean
  {
    if ( !Escalator.hasLiveBody( entity ) )
      return false;

    const p = entity.body.translation();

    if ( p.y <= this.surface_y || this.table.isOutside( p ) )
      return false;

    const v = entity.body.linvel();
    const along = v.x * this.direction.x + v.y * this.direction.y;

    return along < this.getSpeed() * Escalator.STALL_SPEED_SHARE;
  }

  private updateSpawn( entities: readonly LevelEntity[] ): void
  {
    if ( this.breath_left > 0 )
      return;

    const zone = this.spawnZone();

    if ( this.isZoneBusy( entities, zone.left_x, zone.right_x ) )
      return;

    if ( this.wave_left <= 0 )
    {
      this.wave_left = this.rollWave();
      this.breath_left = this.settings.breath_sec;

      if ( this.breath_left > 0 )
        return;
    }

    const width = this.upcoming.cols * this.step_x;
    const anchor = new THREE.Vector3( zone.structure_left_x + width * 0.5, this.surface_y, this.center_z );

    this.launch( this.spawn_structure( this.upcoming.layers, anchor ) );
    this.upcoming = this.structures.next();
    this.wave_left--;

    if ( this.wave_left > 0 )
      this.breath_left = this.settings.block_gap_sec;
  }

  private rollWave(): number
  {
    const { wave_min, wave_max } = this.settings;

    return wave_min + Math.floor( Math.random() * ( wave_max - wave_min + 1 ) );
  }

  private spawnZone(): { structure_left_x: number; left_x: number; right_x: number }
  {
    const width = this.upcoming.cols * this.step_x;
    const gap = this.config.gapCubes * this.step_x;
    const structure_left_x = this.mirrored ? this.spawn_edge_x : this.spawn_edge_x - width;
    const left_x = this.mirrored ? structure_left_x - gap : structure_left_x;
    const right_x = this.mirrored ? structure_left_x + width : this.spawn_edge_x + gap;

    return { structure_left_x, left_x, right_x };
  }

  private static hasLiveBody( entity: LevelEntity ): boolean
  {
    return entity.isAlive() && entity.hasPhysicsBody?.() !== false;
  }

  private isInZone( entity: LevelEntity, left_x: number, right_x: number ): boolean
  {
    if ( !Escalator.hasLiveBody( entity ) )
      return false;

    const margin = this.step_x;
    const p = entity.body.translation();

    return p.y > this.surface_y && p.x > left_x - margin && p.x < right_x + margin;
  }

  private isZoneBusy( entities: readonly LevelEntity[], left_x: number, right_x: number ): boolean
  {
    return entities.some( e => this.isInZone( e, left_x, right_x ) );
  }
}
